/**
 * Filtro por tipo de infraestructura - V05.2
 * PTEL - Sistema de Normalización de Coordenadas
 */

import React, { memo, useCallback, useMemo } from 'react';
import { Checkbox } from '../../ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { InfrastructureType } from '../../../types/infrastructure';
import type { MarkerGroup } from '../../../constants/markerConfig';
import { MARKER_CONFIGS, MARKER_GROUPS } from '../../../constants/markerConfig';
import { getTypesInGroup } from './filterUtils';

export interface TypeFilterProps {
  selectedTypes: InfrastructureType[];
  expandedGroups: MarkerGroup[];
  counts: Partial<Record<InfrastructureType, number>>;
  onToggleType: (type: InfrastructureType) => void;
  onToggleGroup: (group: MarkerGroup) => void;
  onToggleGroupExpanded: (group: MarkerGroup) => void;
}

interface GroupedType {
  type: InfrastructureType;
  config: typeof MARKER_CONFIGS[InfrastructureType];
}

export const TypeFilter = memo<TypeFilterProps>(function TypeFilter({
  selectedTypes,
  expandedGroups,
  counts,
  onToggleType,
  onToggleGroup,
  onToggleGroupExpanded,
}) {
  // Agrupar tipos por grupo
  const groupedTypes = useMemo(() => {
    const groups: Record<MarkerGroup, GroupedType[]> = {
      emergency: [],
      health: [],
      education: [],
      public: [],
      infrastructure: [],
      other: [],
    };

    Object.entries(MARKER_CONFIGS).forEach(([_, config]) => {
      groups[config.group].push({ type: config.type, config });
    });

    return groups;
  }, []);

  // Verificar si un grupo está completamente seleccionado
  const isGroupFullySelected = useCallback((group: MarkerGroup) => {
    const typesInGroup = getTypesInGroup(group);
    if (typesInGroup.length === 0) return false;
    // Si selectedTypes está vacío, todos están "seleccionados" (se muestran todos)
    if (selectedTypes.length === 0) return false;
    return typesInGroup.every(t => selectedTypes.includes(t));
  }, [selectedTypes]);

  // Verificar si un grupo está parcialmente seleccionado
  const isGroupPartiallySelected = useCallback((group: MarkerGroup) => {
    if (selectedTypes.length === 0) return false;
    const typesInGroup = getTypesInGroup(group);
    const selected = typesInGroup.filter(t => selectedTypes.includes(t));
    return selected.length > 0 && selected.length < typesInGroup.length;
  }, [selectedTypes]);

  // Contar total de infraestructuras en un grupo
  const getGroupCount = useCallback((group: MarkerGroup) => {
    const typesInGroup = getTypesInGroup(group);
    return typesInGroup.reduce((sum, type) => sum + (counts[type] || 0), 0);
  }, [counts]);

  return (
    <div className="ptel-type-filter space-y-1">
      {MARKER_GROUPS.map((groupMeta) => {
        const group = groupMeta.id;
        const types = groupedTypes[group];
        if (types.length === 0) return null;

        const isExpanded = expandedGroups.includes(group);
        const groupCount = getGroupCount(group);
        const isFullySelected = isGroupFullySelected(group);
        const isPartiallySelected = isGroupPartiallySelected(group);

        return (
          <Collapsible
            key={group}
            open={isExpanded}
            onOpenChange={() => onToggleGroupExpanded(group)}
          >
            <div className="flex items-center gap-2 py-2 px-2 rounded-md hover:bg-stone-800/50">
              {/* Checkbox del grupo */}
              <Checkbox
                checked={isFullySelected}
                data-state={isPartiallySelected ? 'indeterminate' : isFullySelected ? 'checked' : 'unchecked'}
                onCheckedChange={() => onToggleGroup(group)}
                className="border-stone-600"
              />
              
              {/* Trigger para expandir */}
              <CollapsibleTrigger className="flex-1 flex items-center justify-between text-left">
                <span className="text-sm font-medium text-stone-300">
                  {groupMeta.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 font-mono">
                    ({groupCount})
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-stone-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-stone-500" />
                  )}
                </div>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent>
              <div className="ml-6 space-y-1 pb-2">
                {types.map(({ type, config }) => {
                  const count = counts[type] || 0;
                  // Si selectedTypes vacío = todos visibles; si hay selección, verificar inclusión
                  const isSelected = selectedTypes.length === 0 || selectedTypes.includes(type);

                  return (
                    <label
                      key={type}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-stone-800/30 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedTypes.length === 0 ? false : isSelected}
                        onCheckedChange={() => onToggleType(type)}
                        className="border-stone-600"
                      />
                      <span className="text-base" title={config.ariaLabel}>
                        {config.icon}
                      </span>
                      <span className="flex-1 text-sm text-stone-400">
                        {config.label}
                      </span>
                      <span className="text-xs text-stone-600 font-mono">
                        {count}
                      </span>
                    </label>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
});

export default TypeFilter;
